import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { AuthProvider } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { AccountBootstrap } from "@/components/account-bootstrap";
import { ErrorMonitor } from "@/components/error-monitor";
import { OfflineBanner } from "@/components/offline-banner";
import { reportError } from "@/lib/error-monitoring";
import { ThemeProvider, themeBootstrapScript } from "@/lib/theme";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 font-display text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This corner of Xamseen doesn't exist yet.
        </p>
        <a href="/" className="mt-6 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          Back to feed
        </a>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportError("route_error", error);
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">Please try again or return home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >Try again</button>
          <a href="/" className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Go home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#151517", media: "(prefers-color-scheme: dark)" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "Xamseen" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "format-detection", content: "telephone=no" },
      { title: "Xamseen — See it. Share it. Shape it." },
      { name: "description", content: "Xamseen is a global community platform where people connect, share what is happening around them, report issues, discover solutions, join projects and turn participation into real-world impact." },
      { property: "og:title", content: "Xamseen — See it. Share it. Shape it." },
      { property: "og:description", content: "Xamseen is a global community platform where people connect, share what is happening around them, report issues, discover solutions, join projects and turn participation into real-world impact." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Xamseen" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Xamseen — See it. Share it. Shape it." },
      { name: "twitter:description", content: "Xamseen is a global community platform where people connect, share what is happening around them, report issues, discover solutions, join projects and turn participation into real-world impact." },

    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Light UI: dark lockup on white. Dark UI: white lockup on black.
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-light-16.png", media: "(prefers-color-scheme: light)" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-light-32.png", media: "(prefers-color-scheme: light)" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-light-48.png", media: "(prefers-color-scheme: light)" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon-light.png", media: "(prefers-color-scheme: light)" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png", media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png", media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png", media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png", media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/x-icon", href: "/favicon-light.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "stylesheet", href: "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;900&family=Plus+Jakarta+Sans:wght@400;500;600&display=swap" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://xamseen.com/#organization",
              name: "Xamseen",
              url: "https://xamseen.com",
              slogan: "See it. Share it. Shape it.",
              logo: "https://xamseen.com/icon-512.png",
            },
            {
              "@type": "WebSite",
              "@id": "https://xamseen.com/#website",
              name: "Xamseen",
              url: "https://xamseen.com",
              description:
                "Xamseen is a global community platform where people connect, share what is happening around them, report issues, discover solutions, join projects and turn participation into real-world impact.",
              publisher: { "@id": "https://xamseen.com/#organization" },
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: themeBootstrapScript }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <OfflineBanner />
        <AccountBootstrap />
        <ErrorMonitor />
        <Outlet />
        <Toaster richColors closeButton position="top-right" />
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
